import React, { useState } from "react";
import { SOLUTIONS } from "./solutionsData";
import MoodChart from "./MoodChart";
import DailyCheckIn from "./DailyCheckIn";
import TherapistDashboard from "./TherapistDashboard";
import VoiceRecorder from "./VoiceRecorder";

const PRO_PASSWORD = "%3YVM%+V$y2EwEG"; // Store securely in env for production

const badgeEmojis = {
  "Sunbeam Starter": "🌅",
  "Golden Glow": "🌞",
  "Radiant Ripple": "🌊",
  "Sunny Spirit": "😎",
  "Sunshine Star": "⭐️",
  "Solar Flare": "🔥",
  "Sun Guardian": "🛡️"
};

function hasCheckedInToday(dailyLogs) {
  const today = new Date().toISOString().slice(0, 10);
  return dailyLogs.some(log => log.date === today);
}

const linkStyle = {
  background: "#f7f7f7",
  borderRadius: 16,
  padding: 20,
  textDecoration: "none",
  color: "#222",
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  fontWeight: 600,
};

const avatarOptions = ["🙂", "😃", "😎", "😍", "😇", "😡", "😢", "🥳"];

const emotionPrompts = {
  happy: [
    "What made you feel happy today?",
    "How can you bring more moments like this into your life?",
    "Who or what contributed to your good mood?"
  ],
  sad: [
    "What's been weighing on your mind lately?",
    "Can you remember when you started feeling this way?",
    "Is there something you wish you could change?"
  ],
  angry: [
    "What happened just before you felt angry?",
    "Is there something you wish you could express?",
    "How can you channel this energy positively?"
  ],
  fearful: [
    "What’s making you feel uneasy right now?",
    "Is there something you’re worried about in the future?",
    "What would help you feel safer or more secure?"
  ],
  neutral: [
    "Would you like to talk about anything in particular?",
    "Is there something on your mind you haven’t shared yet?",
    "How are you feeling overall today?"
  ],
  calm: [
    "What’s helping you feel calm?",
    "How can you maintain this sense of peace?",
    "Who or what supports your calmness?"
  ],
  disgust: [
    "What’s causing this reaction?",
    "Is there something you want to avoid or change?",
    "How can you address these feelings?"
  ],
  surprised: [
    "What surprised you recently?",
    "How did you react to the surprise?",
    "Was it a positive or negative surprise?"
  ],
  unknown: [
    "Would you like to try recording again?",
    "How are you feeling right now?",
    "Is there something you want to talk about?"
  ]
};

function getPromptsForEmotion(emotion) {
  return emotionPrompts[emotion] || emotionPrompts["unknown"];
}

function BadgePopup({ newBadge, onClose }) {
  if (!newBadge) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,0.3)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999
      }}
      tabIndex={-1}
    >
      <div style={{
        background: "#fffbe6",
        borderRadius: 18,
        padding: 36,
        boxShadow: "0 4px 32px rgba(255,209,102,0.18)",
        textAlign: "center"
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>
          {badgeEmojis[newBadge] || "🏅"}
        </div>
        <h2>🎉 New Badge Unlocked!</h2>
        <div style={{ fontSize: 20, margin: "12px 0" }}>
          <b>{newBadge}</b>
        </div>
        <button
          onClick={onClose}
          style={{
            marginTop: 16,
            padding: "10px 28px",
            borderRadius: 16,
            background: "#FFD166",
            border: "none",
            fontWeight: 600,
            cursor: "pointer"
          }}
          aria-label="Close badge popup"
        >
          Awesome!
        </button>
      </div>
    </div>
  );
}

function AvatarPicker({ avatar, onSetAvatar, onClose }) {
  return (
    <div style={{
      position: "absolute",
      left: 0,
      top: 38,
      background: "#fff",
      border: "1px solid #ddd",
      borderRadius: 8,
      padding: 8,
      zIndex: 100,
      display: "flex",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
    }}>
      {avatarOptions.map(opt => (
        <span
          key={opt}
          style={{
            fontSize: 28,
            margin: 4,
            cursor: "pointer",
            transition: "transform 0.1s",
            transform: avatar === opt ? "scale(1.2)" : "scale(1)"
          }}
          onClick={() => {
            onSetAvatar(opt);
            onClose();
          }}
          tabIndex={0}
          aria-label={`Choose avatar ${opt}`}
          onKeyPress={e => e.key === "Enter" && onSetAvatar(opt)}
        >
          {opt}
        </span>
      ))}
      <button
        style={{
          marginLeft: 8,
          background: "none",
          border: "none",
          color: "#888",
          cursor: "pointer"
        }}
        onClick={onClose}
        aria-label="Cancel avatar selection"
      >✕</button>
    </div>
  );
}

export default function SolutionDashboard({
  problems = [],
  onBackToProblems,
  onLogout,
  dailyLogs = [],
  onDailyCheckIn,
  canRetake = false,
  points = 0,
  streak = 0,
  badges = [],
  skills = { stress: 0, anxiety: 0, sunlight: 0 },
  displayName = "Anonymous",
  avatar = "🙂",
  onSetDisplayName,
  onSetAvatar,
  newBadge = null,
  onCloseBadgePopup = () => {}
}) {
  const userSolutions = problems.flatMap(p => SOLUTIONS[p] || []);
  const alreadyCheckedIn = hasCheckedInToday(dailyLogs);

  // Profile edit state
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(displayName);

  // Avatar picker state
  const [editingAvatar, setEditingAvatar] = useState(false);

  // Info popups state
  const [showPointsInfo, setShowPointsInfo] = useState(false);
  const [showSkillsInfo, setShowSkillsInfo] = useState(false);

  // Pro feature modal state
  const [showProModal, setShowProModal] = useState(false);
  const [proPassword, setProPassword] = useState("");
  const [proUnlocked, setProUnlocked] = useState(false);
  const [proError, setProError] = useState("");

  // Voice Emotion Check-in state
  const [voiceEmotion, setVoiceEmotion] = useState(null);

  const handleNameSave = () => {
    if (onSetDisplayName) onSetDisplayName(nameInput.trim() || "Anonymous");
    setEditingName(false);
  };

  // Use the first problem as the main type for skill tracking
  const mainType = problems[0] || "stress";

  return (
    <div
      style={{
        maxWidth: 600,
        margin: "32px auto",
        padding: 24,
        background: "#fff",
        borderRadius: 24,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        position: "relative"
      }}
    >
      {/* Badge Celebration Popup */}
      <BadgePopup newBadge={newBadge} onClose={onCloseBadgePopup} />

      {/* Profile Display with Avatar Picker */}
      <section aria-label="Profile" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
          <span
            style={{ fontSize: 32, marginRight: 10, cursor: "pointer" }}
            title="Click to change avatar"
            onClick={() => setEditingAvatar(true)}
            tabIndex={0}
            aria-label="Edit avatar"
          >
            {avatar}
          </span>
          {editingAvatar && (
            <AvatarPicker
              avatar={avatar}
              onSetAvatar={onSetAvatar}
              onClose={() => setEditingAvatar(false)}
            />
          )}
          <span style={{ fontSize: 22, fontWeight: 700, marginRight: 12 }}>
            {editingName ? (
              <>
                <input
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  style={{ fontSize: 18, padding: 4, borderRadius: 6, border: "1px solid #ccc" }}
                  maxLength={24}
                  aria-label="Edit display name"
                />
                <button onClick={handleNameSave} style={{ marginLeft: 6 }}>Save</button>
                <button onClick={() => setEditingName(false)} style={{ marginLeft: 6 }}>Cancel</button>
              </>
            ) : (
              <>
                {displayName}
                <button
                  onClick={() => setEditingName(true)}
                  style={{
                    marginLeft: 12,
                    fontSize: 14,
                    padding: "2px 8px",
                    borderRadius: 8,
                    border: "none",
                    background: "#eee",
                    cursor: "pointer"
                  }}
                  aria-label="Edit display name"
                >
                  Edit
                </button>
              </>
            )}
          </span>
        </div>
      </section>

      {/* BADGES SECTION */}
      {badges && badges.length > 0 && (
        <section aria-label="Badges" style={{
          margin: "24px 0",
          background: "#fffbe6",
          borderRadius: 16,
          padding: "18px 12px",
          boxShadow: "0 2px 8px rgba(255,209,102,0.08)",
          textAlign: "center"
        }}>
          <h4 style={{ marginBottom: 10, color: "#b48b1e", letterSpacing: 1 }}>
            Your Streak Badges
          </h4>
          <div style={{
            display: "flex",
            gap: 18,
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center"
          }}>
            {badges.map(badge => (
              <div key={badge} style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: 80
              }}>
                <span style={{ fontSize: 34 }}>
                  {badgeEmojis[badge] || "🏅"}
                </span>
                <span style={{
                  fontSize: 13,
                  color: "#b48b1e",
                  marginTop: 2,
                  fontWeight: 600
                }}>
                  {badge}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Gamification: Points, Streak, Skills */}
      <section aria-label="Gamification Bar" className="dashboard-bar">
        <div style={{ position: "relative" }}>
          <strong>⭐ Points:</strong> {points}
          <span
            style={{ marginLeft: 4, cursor: "pointer", color: "#888" }}
            onClick={() => setShowPointsInfo(true)}
            aria-label="What are points?"
            title="What are points?"
          >ℹ️</span>
          {showPointsInfo && (
            <div style={{
              position: "absolute", top: 30, left: 0, zIndex: 10,
              background: "#fff", border: "1px solid #ddd", borderRadius: 8,
              padding: 12, width: 220, boxShadow: "0 2px 12px rgba(0,0,0,0.10)"
            }}>
              <strong>Points</strong><br />
              You earn points for each daily check-in and when you practice your recommended solutions. Points help you track your consistency and progress as you build healthy habits.
              <div style={{ textAlign: "right", marginTop: 8 }}>
                <button onClick={() => setShowPointsInfo(false)} style={{ border: "none", background: "none", color: "#217a53", cursor: "pointer" }}>Close</button>
              </div>
            </div>
          )}
        </div>
        <div style={{ position: "relative" }}>
          <strong>🔥 Streak:</strong> {streak} / 21
        </div>
        <div style={{ position: "relative" }}>
          <strong>Skills:</strong>
          <span style={{ marginLeft: 8 }}>
            💆‍♂️ {skills.stress || 0} | 😰 {skills.anxiety || 0} | 🌞 {skills.sunlight || 0}
          </span>
          <span
            style={{ marginLeft: 4, cursor: "pointer", color: "#888" }}
            onClick={() => setShowSkillsInfo(true)}
            aria-label="What are skills?"
            title="What are skills?"
          >ℹ️</span>
          {showSkillsInfo && (
            <div style={{
              position: "absolute", top: 30, right: 0, zIndex: 10,
              background: "#fff", border: "1px solid #ddd", borderRadius: 8,
              padding: 12, width: 260, boxShadow: "0 2px 12px rgba(0,0,0,0.10)"
            }}>
              <strong>Skills</strong>
              <ul style={{ paddingLeft: 20, margin: "8px 0" }}>
                <li><b>Stress:</b> Each time you practice a stress-relief solution, your <b>Stress Management Skill</b> increases.</li>
                <li><b>Anxiety:</b> Each time you practice an anxiety-management solution, your <b>Anxiety Management Skill</b> increases.</li>
                <li><b>Sunlight:</b> Each time you log sunlight exposure or practice related habits, your <b>Sunlight Wellness Skill</b> increases.</li>
              </ul>
              The more you practice, the higher your skills—and the more you reinforce healthy habits!
              <div style={{ textAlign: "right", marginTop: 8 }}>
                <button onClick={() => setShowSkillsInfo(false)} style={{ border: "none", background: "none", color: "#217a53", cursor: "pointer" }}>Close</button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* --- Voice Emotion Check-in Section --- */}
      <section
        style={{
          margin: "32px 0",
          padding: 24,
          background: "#f0f8ff",
          borderRadius: 16,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
        }}
      >
        <h3>Voice Emotion Check-in</h3>
        <p>
          Record your voice and let the AI help you reflect on your emotions. You can use this any time—even after the 21-day program.
        </p>
        <VoiceRecorder
          onResult={data => {
            console.log("VoiceRecorder result:", data);
            setVoiceEmotion(data?.emotion?.toLowerCase?.() || "unknown");
          }}
        />
        {voiceEmotion && (
          <div style={{ marginTop: 18, fontSize: 18 }}>
            <b>Your emotion:</b>{" "}
            <span style={{
              textTransform: "capitalize",
              fontWeight: 700,
              color: "#217a53"
            }}>
              {voiceEmotion}
            </span>
            <div style={{ marginTop: 12 }}>
              <b>Reflection prompts:</b>
              <ul style={{ marginTop: 6 }}>
                {getPromptsForEmotion(voiceEmotion).map((prompt, idx) => (
                  <li key={idx} style={{ marginBottom: 4 }}>{prompt}</li>
                ))}
              </ul>
            </div>
            <div style={{ marginTop: 16 }}>
              <button
                style={{
                  padding: "8px 20px",
                  borderRadius: 10,
                  background: "#FFD166",
                  border: "none",
                  fontWeight: 600,
                  cursor: "pointer",
                  marginRight: 10
                }}
                onClick={() => setVoiceEmotion(null)}
              >
                Try Again
              </button>
              <button
                style={{
                  padding: "8px 20px",
                  borderRadius: 10,
                  background: "#e3fcec",
                  border: "none",
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                Reflect & Journal
              </button>
            </div>
          </div>
        )}
      </section>

      {/* --- Pro Dashboard Button & Modal --- */}
      <section style={{ margin: "28px 0 10px 0", textAlign: "center" }}>
        <button
          onClick={() => setShowProModal(true)}
          style={{
            opacity: 0.5,
            pointerEvents: "auto",
            padding: "10px 24px",
            fontWeight: "bold",
            borderRadius: 12,
            backgroundColor: "#FFD166",
            border: "none",
            cursor: "pointer"
          }}
          title="Pro Feature - Locked"
          aria-label="Therapist Pro Dashboard (locked)"
        >
          🔒 Therapist Pro Dashboard
        </button>
        {showProModal && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: "fixed",
              top: 0, left: 0, right: 0, bottom: 0,
              background: "rgba(0,0,0,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999
            }}
            tabIndex={-1}
          >
            <div style={{
              background: "#fff",
              borderRadius: 16,
              padding: 32,
              minWidth: 320,
              textAlign: "center"
            }}>
              {!proUnlocked ? (
                <>
                  <h3>🔒 Therapist Dashboard</h3>
                  <p>Enter the Pro password to unlock advanced analytics.</p>
                  <input
                    type="password"
                    value={proPassword}
                    onChange={e => {
                      setProPassword(e.target.value);
                      setProError("");
                    }}
                    style={{
                      margin: "12px 0",
                      padding: "8px",
                      borderRadius: 8,
                      border: "1px solid #ccc",
                      width: "80%"
                    }}
                    autoFocus
                    aria-label="Pro password"
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        if (proPassword === PRO_PASSWORD) {
                          setProUnlocked(true);
                        } else {
                          setProError("Incorrect password");
                        }
                      }
                    }}
                  />
                  <div>
                    <button
                      style={{
                        marginRight: 10,
                        padding: "8px 24px",
                        borderRadius: 12,
                        background: "#FFD166",
                        border: "none",
                        fontWeight: 600,
                        cursor: "pointer"
                      }}
                      onClick={() => {
                        if (proPassword === PRO_PASSWORD) {
                          setProUnlocked(true);
                        } else {
                          setProError("Incorrect password");
                        }
                      }}
                    >
                      Unlock
                    </button>
                    <button
                      style={{
                        padding: "8px 24px",
                        borderRadius: 12,
                        background: "#eee",
                        border: "none",
                        fontWeight: 600,
                        cursor: "pointer"
                      }}
                      onClick={() => {
                        setShowProModal(false);
                        setProPassword("");
                        setProUnlocked(false);
                        setProError("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                  {proError && (
                    <div style={{ color: "#ef476f", marginTop: 8 }}>{proError}</div>
                  )}
                </>
              ) : (
                <>
                  <TherapistDashboard />
                  <button
                    style={{
                      marginTop: 18,
                      padding: "8px 24px",
                      borderRadius: 12,
                      background: "#FFD166",
                      border: "none",
                      fontWeight: 600,
                      cursor: "pointer"
                    }}
                    onClick={() => {
                      setShowProModal(false);
                      setProPassword("");
                      setProUnlocked(false);
                      setProError("");
                    }}
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </section>

      <h2 style={{ textAlign: "center", marginBottom: 24 }}>
        Your Personalized Solutions
      </h2>

      {/* Solutions List */}
      {(() => {
        const today = new Date().toISOString().slice(0, 10);
        const practicedToday = dailyLogs.some(
          log => log.date === today && log.didPractice
        );

        if (practicedToday) {
          return (
            <div style={{
              textAlign: "center",
              margin: "32px 0",
              background: "#f0f8ff",
              borderRadius: 12,
              padding: 24,
              fontSize: 18,
              color: "#217a53",
              fontWeight: 600
            }}>
              🎉 Great job practicing today!<br />
              Come back tomorrow for more progress.
            </div>
          );
        } else if (userSolutions.length === 0) {
          return (
            <div style={{ textAlign: "center", margin: "32px 0" }}>
              No solutions found. Please select at least one problem.
            </div>
          );
        } else {
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                marginBottom: 32,
              }}
            >
              {userSolutions.map((sol, idx) =>
                sol.internal ? (
                  <a
                    key={idx}
                    href={sol.internal}
                    style={linkStyle}
                    aria-label={`Open internal solution: ${sol.title}`}
                  >
                    <strong>{sol.title}</strong>
                    <div
                      style={{ marginTop: 6, color: "#666", fontWeight: 400 }}
                    >
                      {sol.desc}
                    </div>
                  </a>
                ) : sol.link ? (
                  <a
                    key={idx}
                    href={sol.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={linkStyle}
                    aria-label={`Open external solution: ${sol.title}`}
                  >
                    <strong>{sol.title}</strong>
                    <div
                      style={{ marginTop: 6, color: "#666", fontWeight: 400 }}
                    >
                      {sol.desc}
                    </div>
                  </a>
                ) : null
              )}
            </div>
          );
        }
      })()}

      {/* Mood Chart */}
      <section style={{ margin: "32px 0" }}>
        <h3 style={{ marginBottom: 12 }}>Your Mood Chart</h3>
        <MoodChart dailyLogs={dailyLogs} />
      </section>

      {/* Daily Check-In */}
      <section style={{ margin: "32px 0" }}>
        <h3 style={{ marginBottom: 12 }}>Daily Check-In</h3>
        {alreadyCheckedIn ? (
          <div
            style={{
              background: "#e0e0e0",
              borderRadius: 12,
              padding: 16,
              textAlign: "center",
              color: "#666",
            }}
          >
            You’ve already checked in today!<br />
            <span style={{ fontSize: 14 }}>
              +10 points & streak updated. Come back tomorrow to keep your streak!
            </span>
          </div>
        ) : (
          <DailyCheckIn onSubmit={log => onDailyCheckIn({ ...log, type: mainType })} />
        )}
      </section>

      {/* 21-Day Program Note */}
      <section style={{
        margin: "24px 0 0 0",
        padding: "12px 18px",
        background: "#e3fcec",
        borderRadius: 12,
        color: "#217a53",
        fontSize: 15,
        textAlign: "center"
      }}>
        <strong>Why 21 days?</strong>
        <br />
        Building healthy habits takes time! Check in daily for 21 days to see real progress and unlock your best results.
      </section>

      {/* Only show Back to Problem Selection if canRetake is true */}
      {canRetake && onBackToProblems && (
        <button
          onClick={onBackToProblems}
          style={{
            margin: "16px 0 0 0",
            padding: "12px 32px",
            borderRadius: 24,
            background: "#FFD166",
            color: "#222",
            fontWeight: 600,
            border: "none",
            cursor: "pointer",
            display: "block",
            width: "100%",
          }}
          aria-label="Back to problem selection"
        >
          Back to Problem Selection
        </button>
      )}

      {/* Log Out always visible */}
      {onLogout && (
        <button
          onClick={onLogout}
          style={{
            margin: "16px 0 0 0",
            padding: "12px 32px",
            borderRadius: 24,
            background: "#ef476f",
            color: "#fff",
            fontWeight: 600,
            border: "none",
            cursor: "pointer",
            display: "block",
            width: "100%",
          }}
          aria-label="Log out"
        >
          Log Out
        </button>
      )}
    </div>
  );
}
